import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/api";
import {
    ArrowRight, ShieldCheck, LockKeyhole, FileDown, CircleCheck,
} from "lucide-react";

const TRUST = [
    { icon: LockKeyhole, label: "NDA-gated access" },
    { icon: ShieldCheck, label: "Tenant-scoped isolation" },
    { icon: FileDown, label: "Branded PDF exports" },
    { icon: CircleCheck, label: "Evidence integrity" },
];

const HeroPreview = () => (
    <div className="relative">
        <div className="relative rounded-2xl border border-border bg-card p-6 sm:p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <div className="mono-label whitespace-nowrap">CLIENT · Nova Health</div>
                <div className="mono-label whitespace-nowrap">SOC 2 · ISO 27001 · HIPAA</div>
            </div>
            <div className="mt-4 flex items-center gap-4">
                <div
                    className="w-12 h-12 rounded-xl grid place-items-center font-display font-semibold text-lg"
                    style={{
                        backgroundColor: "hsl(var(--accent))",
                        color: "hsl(var(--accent-foreground))",
                    }}
                >
                    NH
                </div>
                <div>
                    <div className="font-display text-xl font-semibold leading-tight">
                        Nova Health · Q3 SOC 2 audit
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        17 of 23 controls complete
                    </div>
                </div>
                <div className="ml-auto text-right">
                    <div className="font-display text-3xl font-semibold">67%</div>
                    <div className="mono-label">progress</div>
                </div>
            </div>
            <div className="mt-6 space-y-2">
                {[
                    { code: "AC-01", title: "Access Control", status: "Complete", tone: "emerald" },
                    { code: "DE-01", title: "Data Encryption", status: "Complete", tone: "emerald" },
                    { code: "IR-01", title: "Incident Response", status: "In progress", tone: "amber" },
                ].map((r) => (
                    <div
                        key={r.code}
                        className="flex items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2"
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="mono-label">{r.code}</span>
                            <span className="font-display text-sm font-medium min-w-0 break-words">
                                {r.title}
                            </span>
                        </div>
                        <span
                            className={`text-xs font-medium px-2 py-1 rounded-full border shrink-0 whitespace-nowrap ${
                                r.tone === "emerald"
                                    ? "status-pos border-emerald-500/40 bg-emerald-500/10"
                                    : "status-warn border-amber-500/40 bg-amber-500/10"
                            }`}
                        >
                            {r.status}
                        </span>
                    </div>
                ))}
            </div>
            <div className="mt-6 rounded-lg border border-dashed border-border p-3 flex items-center gap-3">
                <FileDown className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                <div className="text-xs">
                    <span className="font-medium break-all">auditvanguard_nova_health_soc2-iso27001_20260722.pdf</span>
                    <span className="text-muted-foreground whitespace-nowrap"> · 11.8 KB · 6 pages</span>
                </div>
            </div>
        </div>
    </div>
);

export const HeroSection = () => (
    <section className="landing-hero max-w-7xl mx-auto px-5 sm:px-10 pt-8 sm:pt-10 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7 rise-in">
                <div className="flex flex-wrap items-center gap-2 mb-5">
                    <div
                        className="inline-flex items-center gap-2 mono-label px-3 py-1.5 rounded-full border border-border bg-card"
                        style={{ color: "hsl(var(--heading))" }}
                        data-testid="auditor-ready-badge"
                    >
                        <ShieldCheck className="h-3 w-3" strokeWidth={1.5} />
                        Pre-Audit Readiness
                    </div>
                </div>
                <h1
                    className="font-display hero-h1 font-semibold tracking-tight"
                    data-testid="landing-headline"
                >
                    Get your clients audit-ready before the real auditor walks in.
                </h1>
                <p
                    className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
                    data-testid="landing-subhead"
                >
                    AuditVanguard collects security evidence from Okta, AWS, and GitHub, maps it to SOC 2, ISO 27001, HIPAA, and more, and packages it for the auditor. No last-minute scrambling.
                </p>
                <p
                    className="mt-3 text-sm sm:text-base text-muted-foreground max-w-xl leading-relaxed"
                    data-testid="landing-subhead-2"
                >
                    No SOC 2 report means lost enterprise deals. A failed audit means months of delay.
                </p>
                <p className="mt-3 text-sm text-muted-foreground" data-testid="hero-proof-line">
                    For consultants and compliance firms preparing clients for SOC 2, ISO 27001, HIPAA, PCI DSS, CMMC 2.0, and GDPR.
                </p>
                <p className="mt-2 text-sm font-medium" style={{ color: "hsl(var(--heading))" }} data-testid="hero-contrast-line">
                    We don't scan code and we don't issue the audit. We get you ready for it.
                </p>

                <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
                    <Link to="/register" className="w-full sm:w-auto">
                        <Button
                            size="lg"
                            className="rounded-md font-display px-8 h-[52px] w-full sm:w-auto"
                            data-testid="hero-cta-register"
                            onClick={() =>
                                trackEvent("landing_cta_click", {
                                    location: "hero",
                                    label: "get_started",
                                })
                            }
                        >
                            Start free
                            <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                        </Button>
                    </Link>
                    <a
                        href="#how-it-works"
                        className="inline-flex items-center justify-center min-h-[44px] px-4 text-sm font-medium underline underline-offset-4 text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
                        data-testid="hero-see-how-link"
                        onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth", block: "start" });
                            trackEvent("landing_cta_click", { location: "hero", label: "see_how_it_works" });
                        }}
                    >
                        See how it works
                    </a>
                    <span className="mono-label sm:ml-2">Free demo client on sign-up</span>
                </div>

                <Link
                    to="/pricing"
                    className="mt-4 inline-flex items-center min-h-[44px] rounded-full border border-border/70 bg-card px-5 text-sm text-muted-foreground hover:text-foreground hover:border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="hero-price-chip"
                >
                    $199/month, unlimited clients. Free demo client, no card.
                </Link>

                <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
                    {TRUST.map((t) => (
                        <div key={t.label} className="flex items-start gap-2">
                            <t.icon
                                className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0"
                                strokeWidth={1.5}
                            />
                            <div className="text-xs text-muted-foreground leading-snug">
                                {t.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="lg:col-span-5 rise-in">
                <HeroPreview />
            </div>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="clarity-strip">
            {[
                { label: "What it does", line: "AI maps your evidence to controls and produces branded audit reports.", sub: "One workspace per client, with evidence collection, an auditor portal, and a tamper-evident audit trail, for one flat $199/month and unlimited clients." },
                { label: "Who it's for", line: "Consultants and compliance firms getting clients ready for their next compliance audit." },
                { label: "Works with", line: "Okta · AWS · GitHub · PDF export." },
            ].map((c) => (
                <div key={c.label} className="rounded-xl border border-border/60 bg-card p-4">
                    <div className="font-display font-semibold text-sm">{c.label}</div>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{c.line}</p>
                    {c.sub && <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed" data-testid="differentiation-line">{c.sub}</p>}
                </div>
            ))}
        </div>
    </section>
);
